<template>
    <div class="flex mt-5 mx-auto max-w-7xl px-5">
        <div class="flex flex-col space-y-5 w-full">

            <div class="p-6 bg-sky-500 rounded-lg flex flex-col md:flex-row justify-between">
                <div class="flex flex-col md:flex-row items-baseline">
                    <h2 class="text-2xl leading-none tracking-tight lg:text-3xl text-white">Profile Preview</h2>
                    <p class="text-sm opacity-100 ml-4 text-white">This is how your profile looks to companies.</p>
                </div>
                <button @click="copyProfileLinkToClipboard" class="flex items-center bg-white text-sky-600 font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-gray-100 transition mt-2 md:mt-0">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                    </svg>
                    <span class="ml-2 md:ml-0">Share Profile</span>
                </button>
            </div>

            <div class="flex flex-wrap gap-4">
                <div class="flex-none w-full md:w-1/4">
                    <!-- Container for Profile Picture and Details -->
                    <div class="bg-white shadow-lg rounded-lg p-4 justify-center mb-4">
                        <!-- Profile Picture (Placeholder) -->
                        <div class="w-32 h-32 mx-auto bg-gray-200 rounded-full overflow-hidden mb-2">
                            <!-- Replace with actual image tag -->
                            <img :src="profilePicture" alt="Profile Picture" class="w-full h-full object-cover">
                        </div>
                        <!-- Details Section -->
                        <div class="text-center">
                            <!-- Name and Profile Details -->
                            <h3 class="text-xl font-bold text-gray-800 mb-2">{{ this.profile.first_name }} {{ this.profile.last_name }}</h3>
                            <p class="text-gray-600">{{ this.profile.industry }}</p>
                            <p class="text-gray-600">{{ this.profile.suburb }}, {{ this.profile.state }}</p>
                        </div>
                    </div>
                    <div class="bg-white shadow-lg rounded-lg p-6">
                        <div class="">
                            <div class="font-bold text-gray-500 mb-2 text-center">
                                <p>Profile Details</p>
                            </div>
                            <div class="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" fill="none" />
                                    <text x="12" y="16" text-anchor="middle" font-size="12" fill="currentColor" font-family="Arial" font-weight="bold">$</text>
                                </svg>
                                <p class="text-gray-600">{{ this.profile.rate }} / {{ this.profile.rate_type }}</p>
                            </div>
                            <div class="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" fill="none" />
                                    <path d="M12 6v6l3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                <p class="text-gray-600">{{ $filters.screenedAvailability(this.profile.availability) }}</p>
                            </div>
                            <div class="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="5" y="3" width="14" height="18" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none" />
                                    <circle cx="12" cy="11" r="3" stroke="currentColor" stroke-width="1.5" fill="none" />
                                    <path d="M9 14h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                    <path d="M9 17h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                                </svg>
                                <p class="text-gray-600">{{ this.profile.visa_status  }}</p>
                            </div>
                            <div class="flex items-center mb-2">
                                <!-- Email Icon -->
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3 8l7.879 5.879c.171.171.443.171.614 0L21 8V5c0-1.105-.895-2-2-2H5C3.895 3 3 3.895 3 5v3zm0 1.414l7.879 5.879c.171.171.443.171.614 0L21 9.414V19c0 1.105-.895 2-2 2H5c-1.105 0-2-.895-2-2V9.414z" />
                                </svg>
                                <p class="text-gray-600">{{ this.profile.email }}</p>
                            </div>
                            <div class="flex items-center mb-2">
                                <!-- Phone Icon -->
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.01 15.41l-3.75-.44a1.998 1.998 0 00-1.97 1.23l-1.57 3.29c-3.16-1.42-5.69-3.95-7.11-7.11l3.29-1.57c.74-.35 1.18-1.19 1.23-1.97l-.44-3.75A2 2 0 008.01 4H5c-1.1 0-2 .9-2 2 0 8.28 6.72 15 15 15 1.1 0 2-.9 2-2v-3.01c0-1.01-.85-1.86-1.89-1.98z" />
                                </svg>
                                <p class="text-gray-600">{{ this.profile.phone }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Center Column with 2-Column Layout -->
                <div class="flex-1">
                    <transition name="fade">
                        <div v-if="!loading && !profile.ai_description" class="bg-blue-100 p-6 rounded-lg shadow-lg text-center mb-6">
                            <h3 class="text-xl font-bold text-blue-800 mb-2">Enhance Your Profile with AI</h3>
                            <p class="text-blue-700">
                                Let AI craft a compelling career summary that highlights your strengths and makes you stand out to employers.
                            </p>
                            <div class="mt-4 flex justify-center gap-4">
                                <button @click="generateAIDescription" :disabled="loadingAIDescription" class="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-full transition-colors flex items-center justify-center">
                                    <svg v-if="loadingAIDescription" class="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                                    </svg>
                                    <span v-if="!loadingAIDescription">Generate AI Description</span>
                                    <span v-else>Generating...</span>
                                </button>
                                <button @click="refuseAIDescription" class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-full transition-colors flex items-center justify-center">
                                    No, Thanks
                                </button>
                            </div>
                        </div>
                    </transition>

                    <div class="bg-white shadow-lg rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-2 p-6 text-smaller">
                        <!-- Integrated AI Description Section -->
                        <transition name="fade">
                        <div
                            v-if="profile.ai_description && profile.ai_description != 'DECLINED'"
                            class="sm:col-span-2 mb-6"
                        >
                            <div class="flex items-center justify-between">
                            <h4 class="text-lg font-semibold text-gray-800">Profile Description</h4>
                            <div class="flex gap-2">
                                <button
                                v-if="!editingAIDescription"
                                @click="startEditingAIDescription"
                                title="Edit Profile Description"
                                class="p-2 rounded hover:bg-slate-50 transition-colors"
                                >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                        </svg>

                                </button>
                                <!-- Regenerate Button with Circular Refresh Icon -->
                                <button
                                v-if="!editingAIDescription"
                                @click="regenerateAIDescription"
                                title="Regenerate AI Description"
                                class="p-2 rounded hover:bg-blue-50 transition-colors"
                                >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>

                                </button>
                                <!-- Remove Button with Trash Can Icon -->
                                <button
                                v-if="!editingAIDescription"
                                @click="removeAIDescription"
                                title="Remove AI Description"
                                class="p-2 rounded hover:bg-red-50 transition-colors"
                                >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>

                                </button>
                            </div>
                            </div>
                            <div v-if="editingAIDescription" class="mt-3 space-y-3">
                                <textarea
                                    v-model="aiDescriptionDraft"
                                    rows="6"
                                    maxlength="2000"
                                    class="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                ></textarea>
                                <div class="flex items-center justify-between gap-3">
                                    <span class="text-xs text-gray-500">{{ aiDescriptionDraft.length }}/2000</span>
                                    <div class="flex gap-2">
                                        <button
                                            @click="cancelEditingAIDescription"
                                            :disabled="savingAIDescription"
                                            class="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                            type="button"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            @click="saveAIDescription"
                                            :disabled="savingAIDescription"
                                            class="rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                                            type="button"
                                        >
                                            {{ savingAIDescription ? 'Saving...' : 'Save' }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="mt-2 text-gray-600 whitespace-pre-wrap">{{ profile.ai_description }}</p>
                        </div>
                        </transition>

                        <!-- Column 1: Total Experience, Current Experience -->
                        <div class="mb-6">
                            <h4 class="text-lg font-semibold text-gray-800">Total Experience</h4>
                            <p class="text-gray-600">{{ $filters.screenedDate(profile.updated_total_experience) }}</p>
                        </div>
                        <div class="mb-6">
                            <h4 class="text-lg font-semibold text-gray-800">Current Job Tenure</h4>
                            <p class="text-gray-600">{{ $filters.screenedDate(profile.updated_experience_company) }}</p>
                        </div>

                        <!-- Column 2: Current Company, Past Companies -->
                        <div class="mb-6">
                            <h4 class="text-lg font-semibold text-gray-800">Current Company</h4>
                            <p class="text-gray-600">{{ profile.current_company || 'N/A' }}</p>
                        </div>
                        <div class="mb-6">
                            <h4 class="text-lg font-semibold text-gray-800">Past Companies</h4>
                            <p class="text-gray-600">
                            <span v-if="profile.companiesFormatted && !showMoreCompanies">
                                {{ profile.companiesFormatted }}
                            </span>
                            <span v-else>
                                {{ $filters.screenedCompanies(profile.companies) }}
                            </span>
                            <span v-if="profile.companiesFormatted">
                                <a @click="switchMoreCompanies" class="text-blue-500 hover:text-blue-700 cursor-pointer">
                                ({{ showMoreCompanies ? 'show less' : 'show more' }})
                                </a>
                            </span>
                            </p>
                        </div>

                        <!-- Column 3: Contract Status -->
                        <div class="mb-6">
                            <h4 class="text-lg font-semibold text-gray-800">Contract Status</h4>
                            <p class="text-gray-600">{{ profile.contract_type }}</p>
                        </div>
                        <div class="mb-6">
                            <h4 class="text-lg font-semibold text-gray-800">Resume</h4>
                            <p class><a @click="downloadResume()" class="text-blue-500 hover:text-blue-700 cursor-pointer">Download your resume</a></p>
                        </div>

                        <!-- Column 4: Questions and Answers (Full Width) -->
                        <div v-for="item in responses" :key="item.id" class="mb-6 sm:col-span-2">
                            <h4 class="text-lg font-semibold text-gray-800">{{ item.question }}</h4>
                            <p class="text-gray-600">{{ item.response }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
let apiEndpoint = process.env.VUE_APP_API_BASEURL
import contractorService from '@/services/marketplace/contractor.service';
import genericService from '@/services/marketplace/generic.service';
import _ from 'lodash'

export default {
    name: 'MarketplaceContractorProfileEdit',
    inject: ['toggleLoading'],
    data() {
        return {
            loading: true,
            loadingAIDescription: false,
            savingAIDescription: false,
            editingAIDescription: false,
            aiDescriptionDraft: '',
            showMoreCompanies: false,
            profile: [],
            responses: [],
            profilePicture: 'https://via.placeholder.com/128'
        };
    },
    methods: {
        async copyProfileLinkToClipboard() {
            try {
                await navigator.clipboard.writeText(process.env.VUE_APP_WEBSITE_URL + '/contractors/' + this.profile.uuid);
                this.$root.notify('Your public profile URL has been copied to clipboard.', 'success');
            } catch (err) {
                // Don't do anything really, if it's not copied it's not copied
            }
        },
        refuseAIDescription() {
            let postObject = {
                setting: 'refuseAIDescription'
            }

            this.profile.ai_description = 'DECLINED'
            contractorService.updateContractorSettings(postObject)
                .then(response => {
                    this.profile.ai_description = 'DECLINED'
                })
                .catch((error) => {
                    // this.$root.notify('Something went wrong while updating your account settings, please try again', 'error');
                })
                .finally(() => {
                    // this.toggleLoading(false);
                })
        },
        removeAIDescription() {
            this.toggleLoading(true)
            contractorService.removeAIDescription()
                .then(response => {
                    this.profile.ai_description = null
                    this.editingAIDescription = false
                    this.aiDescriptionDraft = ''
                })
                .catch((error) => {
                    // this.$root.notify('Something went wrong while updating your account settings, please try again', 'error');
                })
                .finally(() => {
                    this.toggleLoading(false);
                })
        },
        async regenerateAIDescription() {
            this.toggleLoading(true)
            contractorService.getDescription()
                .then(response => {
                    if (!_.isNil(response.data.description)) {
                        this.profile.ai_description = response.data.description
                        this.editingAIDescription = false
                        this.aiDescriptionDraft = ''
                        this.$root.notify('Your AI-generated profile description is ready—please review it in your profile.', 'success');
                    }
                })
                .catch((error) => {
                    // Redirect if 401
                    this.$root.notify('We run into some issues while trying to obtain your AI-generated profile description, please try again later.', 'error');
                })
                .finally(() => {
                    this.toggleLoading(false)
                })
        },
        async generateAIDescription() {
            if (_.isNil(this.profile.ai_description)) {
                this.loadingAIDescription = true;
                contractorService.getDescription()
                .then(response => {
                    if (!_.isNil(response.data.description)) {
                        this.profile.ai_description = response.data.description
                        this.editingAIDescription = false
                        this.aiDescriptionDraft = ''
                        this.$root.notify('Your AI-generated profile description is ready—please review it in your profile.', 'success');
                    }
                })
                .catch((error) => {
                    // Redirect if 401
                    this.$root.notify('We run into some issues while trying to obtain your AI-generated profile description, please try again later.', 'error');
                })
                .finally(() => {
                    this.loadingAIDescription = false;
                })
            }
        },
        startEditingAIDescription() {
            this.aiDescriptionDraft = this.profile.ai_description || ''
            this.editingAIDescription = true
        },
        cancelEditingAIDescription() {
            this.aiDescriptionDraft = ''
            this.editingAIDescription = false
        },
        saveAIDescription() {
            this.savingAIDescription = true
            contractorService.updateAIDescription(this.aiDescriptionDraft)
                .then(response => {
                    this.profile.ai_description = response.data.description
                    this.editingAIDescription = false
                    this.aiDescriptionDraft = ''
                    this.$root.notify('Your profile description has been updated.', 'success');
                })
                .catch((error) => {
                    const message = error?.response?.data?.message || 'Something went wrong while updating your profile description, please try again';
                    this.$root.notify(message, 'error');
                })
                .finally(() => {
                    this.savingAIDescription = false
                })
        },
        switchMoreCompanies() {
            this.showMoreCompanies = !this.showMoreCompanies
        },
        downloadResume() {
            this.toggleLoading(true);
            contractorService.getResume()
            .then(response => {
                const contentType = response.headers.get('content-type');
                let extension;
                switch(contentType) {
                    case 'application/pdf':
                        extension = 'pdf'
                        break;
                    case 'application/msword':
                        extension = 'doc'
                        break;
                    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                        extension = 'docx'
                        break
                }

                if (response.status === 200 && (contentType === 'application/pdf' ||
                    contentType === 'application/msword' ||
                    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {

                    const outputFileName = `${this.profile.profile_hash}.${this.profile.file_extension}`;
                    const myBlob = response.data;  // Blob data from the response
                    const url = window.URL.createObjectURL(myBlob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', outputFileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link); // Clean up
                    window.URL.revokeObjectURL(url); // Revoke the URL after use
                } else {
                    this.$root.notify('Your resume could not be retrieved at this time. Please try again later..', 'error');
                }
            })
            .catch((error) => {
              console.log(error)
              this.$root.notify('Something went wrong while fetching the resume for the selected candidate, please try again', 'error');
            })
            .finally(() => {
              this.toggleLoading(false);
            })
        },
        async getContractorProfile() {
            this.toggleLoading(true);
            try {
                let response = await fetch(apiEndpoint + "/marketplace.php?getContractorAccount", {
                    headers: {
                        'Authorization': 'Bearer ' +  localStorage.getItem('contractorToken'),
                    },
                })

                if (response.ok) {
                    this.profile = await response.json();

                    if (this.profile.photo_hash || this.profile.profile_picture_s3_key) {
                        this.getImage(this.profile.photo_hash, this.profile.profile_picture_s3_key)
                    }

                    this.loading = false

                    await this.getContractorResponses()
                    this.toggleLoading(false);
                } else {
                    this.toggleLoading(false);
                    this.$router.push('/contractor/logout');
                }
            } catch (error) {
                this.toggleLoading(false);
            }
        },
        async getContractorResponses() {
            try {
                let response = await fetch(apiEndpoint + "/marketplace.php?getContractorResponses", {
                    headers: {
                        'Authorization': 'Bearer ' +  localStorage.getItem('contractorToken'),
                    },
                })
                this.responses = await response.json();
                // console.log(this.responses)
            } catch (error) {
                // console.log(error);
            }
        },
        getImage(hash, s3Key) {
            this.profilePicture = genericService.getProfilePictureLink(hash, s3Key, true)
        }
    },
    created() {
        this.getContractorProfile()
    }
}
</script>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
